import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Award, Users, Clock, Globe, CheckCircle, Star, Briefcase, Eye, Lock, Zap, Drone, HardDrive, Camera, Code, Flame, Package, Cpu, ShieldCheck } from 'lucide-react';
import heroBg from '../assets/hero-bg.jpg';
import logo from '../assets/icon.ico';

const features = [
  {
    icon: Shield,
    title: 'Government Certified',
    description: 'Certified and trusted by government agencies for sensitive operations and intelligence work.'
  },
  {
    icon: Award,
    title: '25+ Years of Excellence',
    description: 'Decades of experience in cybersecurity, digital forensics, and intelligence operations.'
  },
  {
    icon: Users,
    title: 'Expert Team',
    description: 'Highly trained professionals with military, intelligence, and cybersecurity backgrounds.'
  },
  {
    icon: Clock,
    title: '24/7 Response',
    description: 'Round-the-clock emergency response for critical security incidents and investigations.'
  },
  {
    icon: Globe,
    title: 'Global Reach',
    description: 'Serving clients worldwide with a strong presence in India and beyond.'
  },
  {
    icon: Briefcase,
    title: 'Classified Clearance',
    description: 'Security-cleared personnel for handling sensitive and classified government operations.'
  }
];

const capabilities = [
  {
    icon: Shield,
    title: 'Cyber Security & Intelligence',
    description: 'Advanced cybersecurity solutions and intelligence gathering for critical infrastructure protection.'
  },
  {
    icon: Eye,
    title: 'Counter-Intelligence & Surveillance',
    description: 'Specialized equipment and services for counter-intelligence operations and surveillance detection.'
  },
  {
    icon: Camera,
    title: 'Surveillance & Monitoring Systems',
    description: 'Comprehensive surveillance solutions with advanced monitoring and networking capabilities.'
  },
  {
    icon: Drone,
    title: 'Drone Operations & Services',
    description: 'Professional drone services with certified pilots for reconnaissance and surveillance missions.'
  },
  {
    icon: HardDrive,
    title: 'Digital Forensics & Recovery',
    description: 'Expert digital forensics, data recovery solutions, and custom software development.'
  },
  {
    icon: Code,
    title: 'Software Development',
    description: 'Customised, critical, and advanced software development with AI and cloud-native architectures.'
  },
  {
    icon: Flame,
    title: 'Fire Fighting System',
    description: 'Comprehensive fire detection, suppression, and emergency response solutions.'
  },
  {
    icon: Package,
    title: 'Warehouse Management',
    description: 'Advanced RFID-based inventory tracking and intelligent warehouse management solutions.'
  },
  {
    icon: Cpu,
    title: 'Research & Development',
    description: 'Custom embedded systems, IoT solutions, and specialized device development.'
  },
  {
    icon: ShieldCheck,
    title: 'Safety Equipment',
    description: 'Comprehensive physical security solutions including access control and barrier technologies.'
  }
];

const stats = [
  {
    number: '25+',
    label: 'Years of Excellence',
    description: 'Serving government and enterprise clients'
  },
  {
    number: '500+',
    label: 'Security Operations',
    description: 'Successfully completed missions'
  },
  {
    number: '24/7',
    label: 'Emergency Response',
    description: 'Round-the-clock availability'
  },
  {
    number: '100%',
    label: 'Mission Success Rate',
    description: 'Proven track record of results'
  }
];

const AboutPage = () => {
  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="section-padding relative overflow-hidden min-h-[60vh] flex items-center" style={{ backgroundImage: `url(${heroBg})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="absolute inset-0 bg-gradient-to-r from-rich-burgundy/90 to-near-black/80 z-0" />
        <div className="absolute inset-0 grid-pattern opacity-20 z-1" />
        <div className="container relative z-10 text-center">
          <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-4xl mx-auto">
            <div className="inline-flex items-center justify-center w-24 h-24 mb-4 p-1">
              <img src={logo} alt="CIARL Logo" className="w-24 h-24" />
            </div>
            <h1 className="heading-font text-5xl md:text-7xl font-bold mb-6 text-off-white">
              About <span className="text-royal-gold">CIARL</span>
            </h1>
            <p className="text-xl md:text-2xl text-gold-light mb-8 font-light tracking-wide">
              Discover our story. Meet the team behind the mission.
            </p>

            <div className="text-xl text-gray-300 max-w-3xl mx-auto space-y-6 mb-8">
              <p>
                At <span className="text-royal-gold font-semibold">CIARL</span>, we are more than a cybersecurity company—we are your trusted partner in navigating the complex world of cyber intelligence, digital forensics, and research-driven security solutions. With over 25 years of collective expertise, our seasoned professionals have built a reputation for delivering high-quality, reliable, and timely services to clients worldwide.
              </p>
              <p>
                Our journey began in India, where a clear vision and unwavering commitment laid the foundation for CIARL’s evolution into a globally recognized leader in cybersecurity. As we expanded, we remained dedicated to one core mission: helping organizations minimize risk and achieve their goals with confidence.
              </p>
              <p>
                From the start, we have focused on careful planning, risk mitigation, and delivering measurable outcomes. Whether you are scaling your security infrastructure, streamlining operations, or enhancing resilience against digital threats, CIARL stands as your strategic ally every step of the way.
              </p>
              <p>
                As technology evolves, so do we. Our story is marked by continuous innovation, adaptability, and a passion for fortifying the digital realm. Today, CIARL proudly serves a diverse range of industries, offering both managed and professional cybersecurity services tailored to each client’s unique needs.
              </p>
              <p>
                We invite you to be part of our story—one built on partnership, innovation, and trust. Together, let’s build a more secure digital future.
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <div className="flex items-center space-x-2 text-royal-gold">
                <Shield className="w-5 h-5" />
                <span className="font-medium">Government Certified</span>
              </div>
              <div className="flex items-center space-x-2 text-royal-gold">
                <Star className="w-5 h-5" />
                <span className="font-medium">Trusted Worldwide</span>
              </div>
              <div className="flex items-center space-x-2 text-royal-gold">
                <Clock className="w-5 h-5" />
                <span className="font-medium">24/7 Response</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>


      {/* Core Capabilities */}
      <section className="section-padding bg-gray-dark">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="text-center mb-16">
            <h2 className="heading-font text-4xl md:text-5xl font-bold mb-6 text-off-white">
              What We <span className="text-royal-gold">Do</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              From advanced cyber defense to digital forensics and aerial intelligence, CIARL delivers comprehensive solutions for a safer digital world.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {capabilities.map((cap, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 + idx * 0.1 }} className="glass-morphism p-6 rounded-16 hover:border-royal-gold transition-all duration-300 group">
                <div className="w-12 h-12 bg-gradient-to-br from-royal-gold to-gold-light rounded-12 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <cap.icon className="w-6 h-6 text-near-black" />
                </div>
                <h3 className="text-lg font-semibold text-off-white mb-2 group-hover:text-royal-gold transition-colors duration-300">{cap.title}</h3>
                <p className="text-gray-300 text-sm leading-relaxed">{cap.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats/Impact Section */}
      {/* <section className="section-padding relative overflow-hidden" style={{ backgroundImage: `url(${heroBg})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>
        <div className="absolute inset-0 bg-gradient-to-r from-rich-burgundy/90 to-near-black/90 z-0" />
        <div className="absolute inset-0 grid-pattern opacity-20 z-1" />
        <div className="container relative z-10">
          <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="text-center mb-16">
            <h2 className="heading-font text-5xl md:text-6xl font-bold mb-6 text-off-white">
              Proven <span className="text-royal-gold">Results</span>
            </h2>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto">
              Our commitment to excellence is reflected in our track record of successful operations, satisfied clients, and continuous innovation in cybersecurity and intelligence services.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 50, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.6, delay: idx * 0.1 }} className="glass-morphism p-8 rounded-24 text-center group hover:scale-105 transition-all duration-300">
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ duration: 0.8, delay: 0.3 + idx * 0.1, type: 'spring', stiffness: 200 }} className="text-5xl md:text-6xl font-bold text-royal-gold mb-2 heading-font group-hover:text-gold-light transition-colors duration-300">
                  {stat.number}
                </motion.div>
                <h3 className="text-xl font-semibold text-off-white mb-2 group-hover:text-royal-gold transition-colors duration-300">{stat.label}</h3>
                <p className="text-gray-300 text-sm leading-relaxed">{stat.description}</p>
                <div className="w-12 h-1 bg-gradient-to-r from-royal-gold to-gold-light mx-auto mt-4 rounded-full group-hover:w-16 transition-all duration-300" />
              </motion.div>
            ))}
          </div>
          <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.6 }} className="text-center mt-20">
            <div className="max-w-4xl mx-auto">
              <blockquote className="text-2xl md:text-3xl font-light text-gold-light italic leading-relaxed">
                "Excellence in cybersecurity is not just about technology – it's about trust, expertise, and an unwavering commitment to protecting what matters most."
              </blockquote>
              <div className="mt-6">
                <div className="w-24 h-1 bg-royal-gold mx-auto rounded-full" />
                <p className="text-gray-300 mt-4 font-medium">CIARL Leadership Team</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section> */}

      {/* Values & Call to Action */}
      {/* <section className="section-padding bg-rich-burgundy/20">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="text-center">
            <div className="glass-morphism rounded-24 p-12 max-w-4xl mx-auto">
              <h2 className="heading-font text-4xl md:text-5xl font-bold mb-6 text-off-white">
                Join Our <span className="text-royal-gold">Journey</span>
              </h2>
              <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                Our story is one of constant evolution. We continuously adapt to dynamic technological advancements and paradigm shifts, fueled by our passion to fortify the digital realm. We invite you to join us on this journey of partnership, innovation, and trust-building.
              </p>
              <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6">
                <a href="/#contact" className="cta-button">
                  Connect with CIARL
                </a>
                <a href="/services" className="secondary-button">
                  Explore Our Services
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section> */}
    </div>
  );
};

export default AboutPage;